import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ArrowUpRight, Phone, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/4q0pq3ne_34.jpeg';

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About Us' },
  { path: '/products', label: 'Products' },
  { path: '/services', label: 'Services' },
  { path: '/blog', label: 'Blog' },
  { path: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-100 shadow-sm"
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-24">

            {/* ── LOGO — white bg logo with multiply blend removes white on white navbar ── */}
            <Link to="/" data-testid="navbar-logo" className="flex-shrink-0">
              <img
                src={LOGO_URL}
                alt="Seva Gases — Energy for Engineering"
                className="h-[78px] w-auto"
                style={{ mixBlendMode: 'multiply' }}
              />
            </Link>

            {/* ── DESKTOP ONLY: separator + nav links ── */}
            <div className="hidden lg:flex items-center flex-1 ml-0">
              <div className="h-10 w-px bg-gray-300 mx-8 flex-shrink-0" />
              <div className="flex items-center gap-7">
                {navLinks.map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`relative text-[13.5px] font-semibold tracking-wide py-1 transition-colors duration-200 group ${
                      location.pathname === link.path
                        ? 'text-[#CC2222]'
                        : 'text-gray-700 hover:text-[#CC2222]'
                    }`}
                  >
                    {link.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-[2px] bg-[#CC2222] transition-all duration-300 ${
                        location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}
                    />
                  </Link>
                ))}
              </div>
            </div>

            {/* ── DESKTOP: CTA button ── */}
            <div className="hidden lg:flex ml-auto">
              <Link
                to="/contact"
                data-testid="nav-quote-btn"
                className="inline-flex items-center gap-2 bg-[#CC2222] text-white px-6 py-3 text-[13px] font-bold uppercase tracking-wider hover:bg-[#A81C1C] transition-colors duration-200 group"
              >
                Get a Quote
                <ArrowUpRight size={13} className="group-hover:rotate-12 transition-transform duration-200" />
              </Link>
            </div>

            {/* ── MOBILE ONLY: hamburger ── */}
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="navbar-hamburger-btn"
              aria-label="Open menu"
              className="lg:hidden ml-auto flex flex-col justify-center gap-[5px] w-11 h-11 border border-gray-200 hover:border-[#CC2222] hover:text-[#CC2222] text-gray-600 transition-all duration-200 items-center"
            >
              <span className="block h-[1.5px] w-5 bg-current" />
              <span className="block h-[1.5px] w-3.5 bg-current" />
              <span className="block h-[1.5px] w-5 bg-current" />
            </button>

          </div>
        </div>
      </nav>

      {/* ══ MOBILE SIDE DRAWER ══ */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
              onClick={() => setDrawerOpen(false)}
            />

            <motion.div
              key="drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[380px] bg-[#181818] flex flex-col"
              data-testid="nav-drawer"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <img
                  src={LOGO_URL}
                  alt="Seva Gases"
                  className="h-[60px] w-auto"
                  style={{ mixBlendMode: 'screen' }}
                />
                <button
                  onClick={() => setDrawerOpen(false)}
                  data-testid="menu-close-btn"
                  className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/50 transition-all"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Nav links */}
              <div className="flex-1 px-6 pt-8 pb-4 overflow-y-auto">
                <p className="text-white/25 text-[10px] uppercase tracking-[0.3em] font-bold mb-5">Menu</p>
                <nav className="space-y-0">
                  {navLinks.map((link, i) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.07 + i * 0.06, duration: 0.3 }}
                    >
                      <Link
                        to={link.path}
                        onClick={() => setDrawerOpen(false)}
                        data-testid={`drawer-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center justify-between py-4 border-b border-white/[0.07] group transition-all duration-200 ${
                          location.pathname === link.path ? 'text-[#CC2222] pl-2' : 'text-white/75 hover:text-white hover:pl-2'
                        }`}
                      >
                        <span className="text-xl font-bold tracking-tight font-heading">{link.label}</span>
                        <ArrowUpRight size={15} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    </motion.div>
                  ))}
                </nav>
              </div>

              {/* Drawer footer */}
              <div className="px-6 py-6 border-t border-white/10 space-y-3">
                <div className="space-y-2">
                  <a href="tel:+919894020509" className="flex items-center gap-3 text-white/40 hover:text-white/75 transition-colors text-sm">
                    <Phone size={13} className="text-[#CC2222] flex-shrink-0" /> +91 9894020509
                  </a>
                  <a href="mailto:info@sevagases.com" className="flex items-center gap-3 text-white/40 hover:text-white/75 transition-colors text-sm">
                    <Mail size={13} className="text-[#CC2222] flex-shrink-0" /> info@sevagases.com
                  </a>
                </div>
                <Link
                  to="/contact"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center justify-center gap-2 bg-[#CC2222] text-white py-3.5 font-bold text-sm uppercase tracking-wider hover:bg-[#A81C1C] transition-colors"
                >
                  Get a Quote <ArrowUpRight size={13} />
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
